import {
  ActivityFilterFields,
  activityFocusOptions,
  activitySpecificityOptions,
  activityTypeOptions,
  difficultyOptions,
  type FilterOption,
} from '@/Activities/Dashboard/ActivityFilterFields';
import { parseStorage, saveStorage } from '@/Util/utils';

// The Progression tab filters activities with exactly the same fields the Activities tab does (type,
// duration, difficulty, XSS, focus, specificity, favourites, ...) and sends them to the server with
// the same parameter names, so filterActivities() can be reused there. What differs is the date
// range: it is never empty, it is chosen from presets that are relative to *today in the athlete's
// timezone*, and it doubles as the range the charts are drawn over.

export type ProgressionDatePreset = '2w' | '6w' | '3m' | 'ytd' | '1y' | 'all' | 'custom';

// Same ranges the legacy period dropdown offered (ShowsUserTrait::getTimeFilters), plus "custom".
export const progressionDatePresets: FilterOption<Exclude<ProgressionDatePreset, 'custom'>>[] = [
  { label: '2 weeks', value: '2w' },
  { label: '6 weeks', value: '6w' },
  { label: '3 months', value: '3m' },
  { label: 'Year to date', value: 'ytd' },
  { label: '1 year', value: '1y' },
  { label: 'All', value: 'all' },
];

export const DEFAULT_DATE_PRESET: ProgressionDatePreset = 'ytd';

const isDatePreset = (value: unknown): value is ProgressionDatePreset =>
  value === 'custom' || progressionDatePresets.some((preset) => preset.value === value);

/**
 * Today as `YYYY-MM-DD` in the given IANA timezone. The browser's own zone is what an athlete who is
 * travelling (or a coach looking at someone else's tab) would otherwise get, and the server reads
 * these dates in the athlete's profile timezone.
 */
export function todayInTimezone(timezone?: string): string {
  return dateInTimezone(new Date(), timezone);
}

/** The calendar date (`YYYY-MM-DD`) a moment falls on in a given IANA timezone (the browser's, when omitted or unknown). */
export function dateInTimezone(date: Date, timezone?: string): string {
  const format = (timeZone?: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  try {
    return format(timezone || undefined);
  } catch (error) {
    // An unknown timezone name; fall back to the browser's.
    return format(undefined);
  }
}

/** Calendar arithmetic on `YYYY-MM-DD` strings, done in UTC so daylight saving can't shift a day. */
function shiftDate(isoDate: string, change: (date: Date) => void): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  change(date);
  return date.toISOString().slice(0, 10);
}

/** Going back N calendar months, stopping at the end of a shorter month (31 May - 3 months = 28 Feb). */
function subtractMonths(date: Date, months: number) {
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() - months);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
}

/** [from, to] for a preset, as `YYYY-MM-DD`; `from` is '' for "All" (no lower bound). */
export function presetDateRange(preset: Exclude<ProgressionDatePreset, 'custom'>, today: string): [string, string] {
  switch (preset) {
    case '2w': return [shiftDate(today, (d) => d.setUTCDate(d.getUTCDate() - 14)), today];
    case '6w': return [shiftDate(today, (d) => d.setUTCDate(d.getUTCDate() - 42)), today];
    case '3m': return [shiftDate(today, (d) => subtractMonths(d, 3)), today];
    case '1y': return [shiftDate(today, (d) => subtractMonths(d, 12)), today];
    case 'ytd': return [`${today.slice(0, 4)}-01-01`, today];
    case 'all': return ['', today];
  }
}

// Fields that make up "the filters" apart from the date range, which is always in force.
const activityFilterNames: (keyof ActivityFilterFields)[] = [
  'minDuration', 'maxDuration', 'minXss', 'maxXss', 'minDifficulty', 'maxDifficulty',
  'activityType', 'selectedFocus', 'selectedSpecificity',
  'favouritesOnly', 'breakthroughsOnly', 'groupRidesOnly',
];

export class ProgressionFilterFields extends ActivityFilterFields {
  // `declare`: this is set by restore(), which the base constructor calls before this class's own
  // field initialisers would run and wipe it.
  declare datePreset: ProgressionDatePreset;
  /**
   * The custom end date was chosen as "Today", so it is remembered as that rather than as the day it
   * happened to be: coming back tomorrow, it is tomorrow. (Set by restore() too, hence `declare`.)
   */
  declare dateToToday: boolean;
  /** Set when a custom range has its start after its end; the range isn't applied until fixed. */
  dateError: boolean = false;
  private timezone: string = '';

  constructor(onChange: () => void, onFieldChange: () => void, timezone: string, storageKey = 'progressionFilter') {
    // Hidden activities are irrelevant here, and there is no forced-favourites page.
    super(onChange, onFieldChange, storageKey, false, false);
    this.timezone = timezone;
    // The base class restored whatever dates were saved; a preset is relative to today, so redo it.
    if (this.datePreset !== 'custom') this.applyPresetDates();
    else if (this.dateToToday) this.dateTo = todayInTimezone(this.timezone);

    const baseIsFilterApplied = this.isFilterApplied;
    this.isFilterApplied = (filterName = null) => {
      if (filterName === 'dateFrom' || filterName === 'dateTo') return this.isDateRangeApplied();
      if (filterName) return baseIsFilterApplied(filterName);
      return activityFilterNames.some((name) => baseIsFilterApplied(name));
    };
  }

  /** The end of the range is shown as "Today" (in place of the date) for every preset, and for a custom range ended on today. */
  showsEndAsToday = (): boolean => this.datePreset !== 'custom' || this.dateToToday;

  /** Whether the date range differs from the default. Not counted as a "filter" (there is always one). */
  isDateRangeApplied = (): boolean => this.datePreset !== DEFAULT_DATE_PRESET;

  /** Query string (without a leading `?`): the activity filters and the date range, nothing to page or sort. */
  toQuery(): string {
    const params = new URLSearchParams(super.toQuery(1, 0));
    ['page', 'perPage', 'sortBy', 'sortOrder', 'search'].forEach((key) => params.delete(key));
    return params.toString();
  }

  private applyPresetDates() {
    if (this.datePreset === 'custom') return;
    [this.dateFrom, this.dateTo] = presetDateRange(this.datePreset, todayInTimezone(this.timezone));
  }

  setDatePreset = (preset: Exclude<ProgressionDatePreset, 'custom'>) => {
    this.datePreset = preset;
    this.dateToToday = false;
    this.dateError = false;
    this.applyPresetDates();
    this.onFieldChange();
    this.apply();
  };

  /** A range picked by dragging across a chart: it becomes the custom date range. */
  setCustomRange = (from: string, to: string) => {
    this.datePreset = 'custom';
    this.dateToToday = false;
    this.dateFrom = from;
    this.dateTo = to;
    this.dateError = false;
    this.onFieldChange();
    this.apply();
  };

  /** From either date input: any hand-picked date makes the range custom. */
  onCustomDateChange = () => {
    this.datePreset = 'custom';
    this.dateToToday = !!this.dateTo && this.dateTo === todayInTimezone(this.timezone);
    // An empty "to" means up to today, but an empty "from" has to stay open-ended ("All").
    this.dateError = !!this.dateFrom && !!this.dateTo && this.dateFrom > this.dateTo;
    this.onFieldChange();
    if (!this.dateError) this.apply();
  };

  /** The Reset button: everything back to defaults, in one reload rather than one per field. */
  resetFilters = () => {
    this.minDuration = null;
    this.maxDuration = null;
    this.minXss = null;
    this.maxXss = null;
    this.minDifficulty = difficultyOptions[0];
    this.maxDifficulty = difficultyOptions[0];
    this.activityType = activityTypeOptions[0];
    this.selectedFocus = activityFocusOptions[0];
    this.selectedSpecificity = activitySpecificityOptions[0];
    this.favouritesOnly = false;
    this.breakthroughsOnly = false;
    this.groupRidesOnly = false;
    this.datePreset = DEFAULT_DATE_PRESET;
    this.dateToToday = false;
    this.dateError = false;
    this.applyPresetDates();
    this.onFieldChange();
    this.apply();
  };

  protected persist() {
    super.persist();
    saveStorage(this.storageKey, 'datePreset', this.datePreset);
    saveStorage(this.storageKey, 'dateToToday', this.dateToToday);
  }

  protected restore() {
    super.restore();
    const saved = parseStorage(this.storageKey)?.datePreset;
    this.datePreset = isDatePreset(saved) ? saved : DEFAULT_DATE_PRESET;
    this.dateToToday = parseStorage(this.storageKey)?.dateToToday === true;
  }
}
